/**
 * Configuración de Autenticación SIGMA API
 *
 * Gestiona:
 * - Autenticación OAuth 2.0
 * - Caché de tokens en memoria y sessionStorage
 * - Renovación automática de tokens
 * - Deduplicación de solicitudes paralelas
 * - Cliente HTTP centralizado para SIGMA
 */

import { https } from '../http';
import { normalizeResponseKeys } from '../utils/responseNormalizer';
import { env } from '@config/env';
import { createApiError, NetworkError } from '../errors';

// ============================================================================
// TIPOS
// ============================================================================

interface SendRequestParams {
  method?: 'get' | 'post' | 'put' | 'delete' | 'patch';
  path: string;
  body?: any;
  withCredentials?: boolean;
  skipAuth?: boolean;
}

interface Headers {
  [key: string]: string;
}

interface TokenResponse {
  access_token: string;
  token_type?: string;
  expires_in?: number;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const TOKEN_STORAGE_KEY = 'sigma_auth_token';
const TOKEN_EXPIRY_KEY = 'sigma_auth_token_expiry';
const RENEWAL_BUFFER_MS = 60 * 1000; // Renovar 1 minuto antes de expirar

// ============================================================================
// VARIABLES DE ESTADO
// ============================================================================

/** Token de acceso actual en memoria */
let sigmaToken: string | null = null;

/** Fecha de expiración del token actual */
let sigmaTokenExpiration: Date | null = null;

/** Promise de solicitud de token en progreso (para deduplicación) */
let tokenPromise: Promise<string> | null = null;

// ============================================================================
// FUNCIONES DE GESTIÓN DE TOKEN
// ============================================================================

/**
 * Carga token desde sessionStorage al iniciar
 */
const loadTokenFromStorage = (): void => {
  try {
    const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const storedExpiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);

    if (storedToken && storedExpiry) {
      const expiryDate = new Date(storedExpiry);
      const now = new Date();

      // Solo cargar si no ha expirado y no está en ventana de renovación
      if (expiryDate.getTime() - now.getTime() > RENEWAL_BUFFER_MS) {
        sigmaToken = storedToken;
        sigmaTokenExpiration = expiryDate;
      }
    }
  } catch (error) {
    console.error('❌ Error cargando token SIGMA desde storage:', error);
  }
};

/**
 * Guarda token en sessionStorage
 */
const saveTokenToStorage = (token: string, expiry: Date): void => {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toISOString());
  } catch (error) {
    console.error('❌ Error guardando token SIGMA en storage:', error);
  }
};

/**
 * Verifica si el token necesita renovarse (expirado o próximo a expirar)
 */
const needsRenewal = (): boolean => {
  if (!sigmaToken || !sigmaTokenExpiration) {
    return true;
  }

  const now = new Date();
  const timeUntilExpiry = sigmaTokenExpiration.getTime() - now.getTime();

  // Renovar si ya expiró o está dentro del buffer de renovación
  return timeUntilExpiry <= RENEWAL_BUFFER_MS;
};

/**
 * Obtiene token de autenticación para Sigma API
 *
 * Características:
 * - Cache en memoria
 * - Persistencia en sessionStorage
 * - Deduplicación de requests paralelos
 * - Renovación anticipada (1 minuto antes de expirar)
 *
 * @returns Token de acceso válido
 */
const getTokenSigma = async (): Promise<string> => {
  // Cargar desde storage si es la primera vez
  if (!sigmaToken && !tokenPromise) {
    loadTokenFromStorage();
  }

  // Si el token es válido y no necesita renovación, retornarlo
  if (!needsRenewal()) {
    return sigmaToken!;
  }

  // Si ya hay una solicitud de token en progreso, esperar a esa
  if (tokenPromise) {
    return tokenPromise;
  }

  // Crear nueva solicitud de token

  const APIURL = env.sigmaApiUrl;
  const CLIENTID = env.sigmaClientId;
  const CLIENTSECRET = env.sigmaClientSecret;

  const auth = btoa(`${CLIENTID}:${CLIENTSECRET}`);
  const key = new URLSearchParams({ grant_type: 'client_credentials' });

  tokenPromise = https<TokenResponse>({
    method: 'post',
    url: `${APIURL}/oauth/token`,
    headers: {
      Authorization: `Basic ${auth}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: key,
  })
    .then((response) => {
      
      sigmaToken = response.access_token;

      // Calcular expiración según el expires_in del response (o 10 min por defecto)
      const expiresInMs = (response.expires_in || 600) * 1000; // expires_in viene en segundos
      sigmaTokenExpiration = new Date(Date.now() + expiresInMs);

      // Guardar en sessionStorage
      saveTokenToStorage(sigmaToken, sigmaTokenExpiration);

      return sigmaToken;
    })
    .catch((error) => {
      console.error('❌ Error obteniendo token Sigma:', error);
      throw error;
    })
    .finally(() => {
      // Limpiar la promise para permitir nuevas solicitudes
      tokenPromise = null;
    });

  return tokenPromise;
};

// ============================================================================
// CLIENTE HTTP SIGMA
// ============================================================================

/**
 * Cliente HTTP genérico para consumir la Sigma API
 *
 * Implementa el patrón de excepciones según Clean Code:
 * - Fail Fast: Lanza excepciones inmediatamente en caso de error
 * - Excepciones tipadas para manejo específico
 * - Sin códigos de retorno para errores
 *
 * Características:
 * - Autenticación OAuth automática
 * - URL base de SIGMA
 * - Headers estándar
 * - Normalización de respuestas (claves a minúsculas)
 * - Excepciones tipadas según código HTTP
 *
 * @param params - Parámetros de la solicitud
 * @returns Respuesta normalizada del servicio
 * @throws {ValidationError} Error 400 - Validación de negocio (ej: -2 sin registros, -777 campos faltantes)
 * @throws {UnauthorizedError} Error 401 - Sin autenticación
 * @throws {ForbiddenError} Error 403 - Sin permisos
 * @throws {NotFoundError} Error 404 - Recurso no encontrado
 * @throws {ServerError} Error 5xx - Error del servidor
 * @throws {NetworkError} Error de red/conexión
 *
 * @example
 * ```ts
 * try {
 *   const paises = await sendRequest({
 *     method: 'post',
 *     path: 'wspais/get',
 *     body: {}
 *   });
 * } catch (error) {
 *   if (error instanceof ValidationError) {
 *     // Código de negocio SIGMA (ej: -2, -777)
 *     console.log('Error de validación:', error.message, error.code);
 *   } else if (error instanceof ServerError) {
 *     // Error del servidor
 *     console.error('Error del servidor:', error);
 *   }
 * }
 * ```
 */
export const sendRequest = async <T = any>(params: SendRequestParams): Promise<T> => {
  const { method = 'post', path, body = {}, withCredentials, skipAuth = false } = params;
  const BASE_URL = env.sigmaApiUrl;

  try {
    const url = `${BASE_URL}/${path}`;

    const headers: Headers = {
      'Content-Type': 'application/json',
    };

    if (!skipAuth) {
      const accessToken = await getTokenSigma();
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await https<T>({
      method,
      url,
      headers,
      body,
      ...(withCredentials !== undefined && { withCredentials }),
    });
    
    // NORMALIZACIÓN CENTRALIZADA: Aplicar normalización a todas las respuestas de Sigma
    // Convierte todas las claves de objetos a minúsculas para consistencia

    if(!path.toLowerCase().includes("valrep")){
      return normalizeResponseKeys(response);

    }else{
      return response;
    }

  } catch (error: any) {
    // Manejo de errores HTTP - Siempre lanzar excepciones (Fail Fast)
    if (error.response) {
      const { status, data } = error.response;

      // Extraer información del error de SIGMA
      // SIGMA usa códigos de negocio en el body (ej: -2, -777)
      const errorMessage = data?.message || data?.description || data?.error;
      const errorCode = data?.code || data?.codigo;

      // Crear y lanzar la excepción tipada apropiada
      throw createApiError(status, errorMessage, { code: errorCode, ...data }, error);
    }

    // Error de red (sin response)
    if (error.request) {
      console.error('❌ Error de red en Sigma API:', error.message);
      throw new NetworkError('No se pudo conectar con el servidor', error);
    }

    // Error desconocido
    console.error('❌ Error desconocido en Sigma API:', error);
    throw error;
  }
};

/**
 * Limpia el token almacenado (útil para logout)
 */
export const clearSigmaToken = (): void => {
  sigmaToken = null;
  sigmaTokenExpiration = null;
  tokenPromise = null;
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
};
